/* eslint-disable react/no-this-in-sfc */
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import darkNavigatorStyles from "components/charts/modern/highcharts/darkNavigatorStyles";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import type { TDividendsPaidByYear } from "components/company/v3/contents/events/dividends/types";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";
import type { SeriesOptionsType } from "highcharts";
import trimEnd from "lodash/trimEnd";
import numeral from "numeral";
import { renderToStaticMarkup } from "react-dom/server";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import type { IDividendsPaidDetails } from "types/graphqlTypes";

interface DividendsPaidByYearChartProps {
  dividendsPaid: TDividendsPaidByYear[];
  currency: "BRL" | "USD";
}

export default function DividendsPaidByYearChart({ dividendsPaid, currency }: DividendsPaidByYearChartProps) {
  const intl = useIntl();
  const appTheme = useTheme();
  const slateOrDarkSlate = appTheme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = appTheme === "dark" ? colors.slate[300] : colors.slate[500];
  const theme = getColorThemeForIndex(0, appTheme);

  if (!theme) {
    throw new Error("Theme not found.");
  }

  const currencyLabel =
    currency === "BRL"
      ? intl.formatMessage({
          defaultMessage: "Valor (R$)",
          id: "XydT1i",
          description: "Valor dos dividendos pagos em reais",
        })
      : intl.formatMessage({
          defaultMessage: "Valor (U$)",
          id: "gHZX1a",
          description: "Valor dos dividendos pagos em dólar",
        });

  const formatAmount = (amount: number) => {
    if (currency === "BRL") {
      return numeral(amount).format("0.00").replace(".", ",");
    }
    return trimEnd(trimEnd(numeral(amount).format("0.000").replace(".", ","), "0"), ",");
  };

  const series: SeriesOptionsType[] = [
    {
      type: "column",
      showInNavigator: true,
      animation: false,
      name: intl.formatMessage({ defaultMessage: "Rendimentos", id: "+8A8mv" }),
      color: theme.color,
      borderColor: theme.lineColor,
      data: [...dividendsPaid]
        .sort((a, b) => a.year - b.year)
        .map((node) => ({
          x: node.year,
          y: node.amountPaid,
          custom: node,
        })),
      custom: {
        primaryColor: theme.color,
        secondaryColor: theme.lineColor,
      },
    },
  ];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      tickColor: slateOrDarkSlate,
      scrollbar: {
        enabled: false,
      },
      labels: {
        formatter() {
          return String(this.value);
        },
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: appTheme === "light" ? colors.slate[800] : colors.slate[500],
      },
    },
    yAxis: {
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: appTheme === "light" ? colors.slate[800] : colors.slate[500],
      },
    },
    series,
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: dividendsPaid.length > 10,
      ...(appTheme === "dark" ? darkNavigatorStyles : navigatorStyles),
      xAxis: {
        ...(appTheme === "dark" ? darkNavigatorStyles : navigatorStyles).xAxis,
        labels: {
          formatter() {
            return String(this.value);
          },
        },
      },
      yAxis: {
        ...(appTheme === "dark" ? darkNavigatorStyles : navigatorStyles).yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        type: "linear",
      },
    },
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: appTheme === "dark" ? colors.slate[800] : colors.white,
      borderColor: appTheme === "dark" ? colors.slate[950] : colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: false,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const point = (this.point as any).custom as IDividendsPaidDetails | null | undefined;
        if (!this.key || !point) {
          return "";
        }

        return renderToStaticMarkup(
          <KeyValueTooltip
            x={String(this.x)}
            keyValues={[
              {
                key: currencyLabel,
                value: formatAmount(point.amountPaid),
              },
            ]}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: appTheme === "dark" ? colors.slate[400] : colors.slate[500],
            color: appTheme === "dark" ? colors.slate[400] : colors.slate[50],
            lineWidth: 2,
          },
        },
      },
      column: {
        dataGrouping: {
          enabled: true,
          approximation: "average",
        },
        stacking: "normal",
        borderWidth: 1.5,
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  return <HighchartsChart options={options} className="h-96 xl:h-full" />;
}
